export const LIVE_RECENCY_OPACITY_MIN = 0.1;
export const LIVE_RECENCY_OPACITY_MAX = 1;
export const LIVE_SELECTION_UNSELECTED_OPACITY = 0.2;
export const LIVE_RECENCY_SIZE_BONUS_PX = 5;
export const LIVE_RECENCY_WINDOW_MS = 60_000;

export interface LiveRecencyPoint {
  Datetime?: string | Date;
  datetime?: string | Date;
  ID?: number | string;
  event_id?: number | string;
}

export interface RecencyOpacityScale {
  getRecency: (point: LiveRecencyPoint) => number;
  isRecent: (point: LiveRecencyPoint) => boolean;
}

export interface BuildRecencyOpacityScaleOptions {
  windowMs?: number;
  /** Defaults to the newest Datetime in `points`, or `Date.now()` when empty. */
  referenceMs?: number;
}

function pointDatetimeMs(point: LiveRecencyPoint): number | null {
  const raw = point.Datetime ?? point.datetime;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function resolveReferenceMs(
  points: LiveRecencyPoint[],
  referenceMs?: number
): number {
  if (referenceMs != null && Number.isFinite(referenceMs)) return referenceMs;
  const times: number[] = [];
  for (const point of points) {
    const ms = pointDatetimeMs(point);
    if (ms != null) times.push(ms);
  }
  return times.length > 0 ? Math.max(...times) : Date.now();
}

/**
 * Build a rolling recency scale: points within `windowMs` of the reference time
 * (newest Datetime in the dataset by default) → full opacity; older → min opacity.
 */
export function buildRecencyOpacityScale(
  points: LiveRecencyPoint[],
  options?: BuildRecencyOpacityScaleOptions
): RecencyOpacityScale {
  const windowMs = options?.windowMs ?? LIVE_RECENCY_WINDOW_MS;
  const referenceMs = resolveReferenceMs(points, options?.referenceMs);

  const isRecentPoint = (point: LiveRecencyPoint): boolean => {
    const ms = pointDatetimeMs(point);
    if (ms == null) return false;
    return referenceMs - ms <= windowMs;
  };

  return {
    isRecent: isRecentPoint,
    getRecency: (point: LiveRecencyPoint) =>
      isRecentPoint(point) ? LIVE_RECENCY_OPACITY_MAX : LIVE_RECENCY_OPACITY_MIN,
  };
}

export interface ResolveScatterPointOpacityOptions {
  recencyScale?: RecencyOpacityScale | null;
  selectedIds: number[];
  selectedOpacity?: number;
  unselectedOpacity?: number;
  liveRecencyEnabled?: boolean;
}

function pointEventId(point: LiveRecencyPoint): number | null {
  const raw = point.ID ?? point.event_id;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Combine live recency opacity with existing selection dimming rules.
 */
export function resolveScatterPointOpacity(
  point: LiveRecencyPoint,
  options: ResolveScatterPointOpacityOptions
): number {
  const selectedOpacity = options.selectedOpacity ?? LIVE_RECENCY_OPACITY_MAX;
  const unselectedOpacity = options.unselectedOpacity ?? LIVE_SELECTION_UNSELECTED_OPACITY;
  const liveRecencyEnabled = options.liveRecencyEnabled === true;
  const recency = liveRecencyEnabled && options.recencyScale
    ? options.recencyScale.getRecency(point)
    : LIVE_RECENCY_OPACITY_MAX;

  const selectedIds = options.selectedIds ?? [];
  const hasSelection = selectedIds.length > 0;
  const id = pointEventId(point);
  const isSelected = id != null && selectedIds.includes(id);

  if (hasSelection) {
    if (isSelected) return selectedOpacity;
    return liveRecencyEnabled ? unselectedOpacity * recency : unselectedOpacity;
  }

  return liveRecencyEnabled ? recency : LIVE_RECENCY_OPACITY_MAX;
}

export interface ResolveScatterPointRecencySizeBonusOptions {
  recencyScale?: RecencyOpacityScale | null;
  liveRecencyEnabled?: boolean;
  maxBonusPx?: number;
}

/** Full size bonus for points inside the live recency window; none for older points. */
export function resolveScatterPointRecencySizeBonus(
  point: LiveRecencyPoint,
  options: ResolveScatterPointRecencySizeBonusOptions
): number {
  const liveRecencyEnabled = options.liveRecencyEnabled === true;
  if (!liveRecencyEnabled || !options.recencyScale) return 0;
  if (!options.recencyScale.isRecent(point)) return 0;
  return options.maxBonusPx ?? LIVE_RECENCY_SIZE_BONUS_PX;
}
